import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { Client, Project, Employee, Timesheet, CostCentre } from '@/types/timesheet';
import { initializeMockData } from '@/data/mockData';
import { generateHistoricalTimesheets } from '@/data/generateHistoricalData';
import { auditService } from '@/services/auditService';
import type { AuditActionType, AuditChange } from '@/types/audit';
import { timesheetApi } from '@/services/timesheetApi';
import { toast } from 'sonner';
import { useDataSource } from './DataSourceContext';
import { generateMockAbsenceEntries, getAbsenceSettings, saveAbsenceSettings } from '@/data/mockAbsenceData';
import { useCompany } from '@/features/multi-company/context/CompanyContext';

interface DataSet {
  clients: Client[];
  projects: Project[];
  employees: Employee[];
  timesheets: Timesheet[];
}

interface TimesheetContextType {
  clients: Client[];
  projects: Project[];
  employees: Employee[];
  timesheets: Timesheet[];
  costCentres: CostCentre[];
  currentEmployee: Employee;
  currentUser: Employee; // Alias for currentEmployee
  pendingApprovals: Timesheet[];
  updateTimesheet: (timesheet: Timesheet) => void;
  deleteTimesheet: (id: number) => void;
  toggleProjectFavourite: (projectId: string) => void;
  toggleCostCentreFavourite: (costCentreId: string) => void;
  updateCostCentreLastUsed: (costCentreId: string) => void;
  switchEmployee: (employeeId: number) => void;
  setCurrentEmployeeFromAuth: (employee: Employee) => void;
  getDemoData: () => DataSet;
  getLiveData: () => DataSet | null;
  getLiveLoading: () => boolean;
  getLiveError: () => string | null;
  ensureLiveDataLoaded: () => Promise<void>;
  refreshEmployeeTimesheets: (employeeId?: number) => Promise<void>;
  updateTimesheetForMode: (mode: 'demo' | 'live', timesheet: Timesheet) => void;
  loadDirectReportTimesheets: () => Promise<void>;
  teamTimesheetsLoading: boolean;
}

const TimesheetContext = createContext<TimesheetContextType | undefined>(undefined);

export const TimesheetProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [demoData, setDemoData] = useState<DataSet>(() => initializeMockData());
  
  const [liveData, setLiveData] = useState<DataSet | null>(null);
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveError, setLiveError] = useState<string | null>(null);
  
  // Synchronous ref to prevent concurrent calls to ensureLiveDataLoaded
  // React state updates are async/batched, so multiple callers in the same render tick
  // can all pass the liveLoading state guard simultaneously. This ref acts as a lock.
  const liveLoadingRef = useRef(false);
  
  // Retry mechanism: track retry count to prevent infinite retries on API failure
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;
  
  // State for lazy loading team timesheets (direct reports)
  const [teamTimesheetsLoaded, setTeamTimesheetsLoaded] = useState(false);
  const [teamTimesheetsLoading, setTeamTimesheetsLoading] = useState(false);
  
  // Load currentEmployeeId from localStorage or default to 1
  const getInitialEmployeeId = (): number => {
    const storedId = localStorage.getItem('currentEmployeeId');
    if (storedId) {
      const parsed = parseInt(storedId, 10);
      return isNaN(parsed) ? 1 : parsed;
    }
    // Default to 1 and store it
    const defaultId = 1;
    localStorage.setItem('currentEmployeeId', String(defaultId));
    return defaultId;
  };
  
  const [currentEmployeeId, setCurrentEmployeeId] = useState<number>(getInitialEmployeeId());
  const [currentEmployee, setCurrentEmployee] = useState<Employee>(
    demoData.employees.find(emp => emp.id === currentEmployeeId) || demoData.employees[0]
  );
  const [costCentres, setCostCentres] = useState<CostCentre[]>([]);
  
  const [data, setData] = useState(demoData);
  
  const { mode, isDemo, getScreenMode } = useDataSource();
  
  // Multi-company support: get current company ID for filtering API calls
  const { currentCompany } = useCompany();
  const currentCompanyId = currentCompany?.id;

  const ensureEmployeeAvatars = (employees: Employee[]): Employee[] => {
    return employees.map(emp => ({
      ...emp,
      photoUrl: emp.photoUrl || `https://picsum.photos/seed/${encodeURIComponent(emp.email || emp.name)}/300/300`
    }));
  };

  const ensureLiveDataLoaded = useCallback(async (overrideEmployeeId?: number, force = false, companyIdOverride?: string) => {
    // Skip if already loading (synchronous ref check prevents concurrent calls),
    // or if there's a prior error and we're not forcing a retry,
    // or if data exists and not forcing a refresh
    if (liveLoadingRef.current || (!force && liveError) || (liveData !== null && !force)) {
      return;
    }
    
    // When forcing, reset error state and retry count
    if (force) {
      setLiveError(null);
      retryCountRef.current = 0;
    }
    
    // Check retry limit (only applies to non-forced automatic retries)
    if (!force && retryCountRef.current >= MAX_RETRIES) {
      return;
    }

    liveLoadingRef.current = true;
    setLiveLoading(true);
    setLiveError(null);

    // Use the override employee ID if provided, otherwise use the current state
    // This fixes a race condition where setCurrentEmployeeFromAuth calls this function
    // immediately after setting state, but React's async state updates mean
    // currentEmployeeId may still have the old value
    const employeeIdToUse = overrideEmployeeId ?? currentEmployeeId;

    // Use the override company ID if provided, otherwise use the current state
    // This fixes a stale closure issue where the company-changed event handler
    // captures the initial ensureLiveDataLoaded, which would use the old currentCompanyId
    const companyIdToUse = companyIdOverride ?? currentCompanyId;

    try {
      
      const [allEmployees, employeeTimesheets, allProjects, allCostCentres] = await Promise.all([
        timesheetApi.getAllEmployees(),
        timesheetApi.getTimesheetsByEmployeeId(employeeIdToUse, companyIdToUse),
        timesheetApi.getAllProjects(companyIdToUse),
        timesheetApi.getAllCostCentres(companyIdToUse)
      ]);
      
      // In live mode, don't add fake avatars — let components fall back to initials.
      // ensureEmployeeAvatars is only used for demo mode data.
      const employeesWithAvatars = allEmployees;
      
      // Note: Direct reports' timesheets are now loaded lazily when the manager homepage is opened
      // This improves initial app load time by avoiding N+1 API calls at startup
      const allTimesheets = employeeTimesheets;
      
      const projectsWithCostCentres = allProjects.map(project => ({
        ...project,
        costCentres: allCostCentres
      }));
      
      const newLiveData: DataSet = {
        clients: [],
        employees: employeesWithAvatars,
        timesheets: allTimesheets,
        projects: projectsWithCostCentres
      };
      
      setLiveData(newLiveData);
      setLiveLoading(false);
      liveLoadingRef.current = false;
      retryCountRef.current = 0; // Reset retry count on success
    } catch (error) {
      console.error('❌ Failed to fetch data from API:', error);
      retryCountRef.current += 1;
      setLiveError('Could not connect to backend. Please ensure the API is running.');
      setLiveLoading(false);
      liveLoadingRef.current = false;
      toast.error('Failed to connect to API', {
        description: retryCountRef.current >= MAX_RETRIES
          ? 'Could not connect to backend after multiple attempts. Please ensure the API is running or switch to demo mode.'
          : `Could not connect to backend (attempt ${retryCountRef.current}/${MAX_RETRIES}). Retrying...`,
      });
      
      // Auto-retry with exponential backoff if under the retry limit
      if (retryCountRef.current < MAX_RETRIES) {
        const backoffMs = Math.pow(2, retryCountRef.current - 1) * 1000; // 1s, 2s, 4s
        setTimeout(() => {
          // Clear the error to allow the retry to proceed
          setLiveError(null);
          ensureLiveDataLoaded(overrideEmployeeId, false, companyIdOverride);
        }, backoffMs);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveData, liveError, currentEmployeeId, currentCompanyId]);

  // Force refetch of live data - used when company changes
  // This function now fetches new data without clearing the old data first,
  // preventing the UI from showing empty dropdowns during the transition
  const forceRefetchLiveData = useCallback(async (companyIdOverride?: string) => {
    setTeamTimesheetsLoaded(false);
    // Fetch new data immediately, keeping old data visible until new data arrives
    await ensureLiveDataLoaded(undefined, true, companyIdOverride);
  }, [ensureLiveDataLoaded]);

  // Lazy load direct reports' timesheets when the manager homepage is opened
  const loadDirectReportTimesheets = async () => {
    // Guard: skip if already loaded, currently loading, or no live data available
    if (teamTimesheetsLoaded || teamTimesheetsLoading || !liveData) {
      return;
    }

    setTeamTimesheetsLoading(true);

    try {
      // Get direct reports from the loaded employees
      const directReports = liveData.employees.filter(emp => emp.managerId === currentEmployeeId);

      if (directReports.length === 0) {
        setTeamTimesheetsLoaded(true);
        setTeamTimesheetsLoading(false);
        return;
      }

      // Use the bulk API endpoint to fetch all direct reports' timesheets in a single request
      // Employee.id is now a number type matching the backend INT type
      const directReportIds = directReports.map(emp => emp.id);
      const directReportTimesheets = await timesheetApi.getTimesheetsByEmployeeIds(directReportIds, currentCompanyId);
      
      // Merge the new timesheets into liveData, avoiding duplicates by ID
      setLiveData(prev => {
        if (!prev) return prev;
        
        const existingIds = new Set(prev.timesheets.map(ts => ts.id));
        const newTimesheets = directReportTimesheets.filter(ts => !existingIds.has(ts.id));
        
        return {
          ...prev,
          timesheets: [...prev.timesheets, ...newTimesheets]
        };
      });

      setTeamTimesheetsLoaded(true);
      setTeamTimesheetsLoading(false);
    } catch (error) {
      console.error('❌ Failed to load direct reports timesheets:', error);
      setTeamTimesheetsLoading(false);
      toast.error('Failed to load team timesheets', {
        description: 'Could not load timesheets for your direct reports.',
      });
    }
  };

  // Re-fetch just the timesheets for a specific employee and merge into liveData.
  // This is used when navigating to a timesheet that may have been updated externally
  // (e.g. rejected by a manager) while the cached liveData still holds a stale status.
  const refreshEmployeeTimesheets = useCallback(async (employeeId?: number) => {
    if (!liveData) return;
    try {
      const empId = employeeId ?? currentEmployeeId;
      const freshTimesheets = await timesheetApi.getTimesheetsByEmployeeId(empId, currentCompanyId);
      setLiveData(prev => {
        if (!prev) return prev;
        // Replace timesheets for this employee, keep others (e.g. direct reports)
        const otherTimesheets = prev.timesheets.filter(ts => ts.employeeId !== empId);
        return {
          ...prev,
          timesheets: [...otherTimesheets, ...freshTimesheets],
        };
      });
    } catch (error) {
      console.error('Failed to refresh employee timesheets:', error);
    }
  }, [liveData, currentEmployeeId, currentCompanyId]);

  // Listen for company-changed event to refetch data when switching companies
  // This ensures data is fetched from the correct integration source (local DB vs external API)
  // We extract companyId from the event detail and pass it through as a parameter
  // to avoid stale closure issues (the handler captures the initial function references,
  // but the companyId flows as a parameter bypassing the stale closure)
  useEffect(() => {
    const handleCompanyChange = (event: Event) => {
      const companyId = (event as CustomEvent).detail?.companyId as string | undefined;
      forceRefetchLiveData(companyId);
    };

    window.addEventListener('company-changed', handleCompanyChange);
    return () => window.removeEventListener('company-changed', handleCompanyChange);
  }, []);

  // Load cost centre favorites from localStorage
  useEffect(() => {
    const loadCostCentreFavourites = () => {
      try {
        const stored = localStorage.getItem(`costCentreFavourites_${currentEmployee.id}`);
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (error) {
        console.error('Failed to load cost centre favourites:', error);
      }
      return [];
    };

    const loadCostCentreLastUsed = () => {
      try {
        const stored = localStorage.getItem(`costCentreLastUsed_${currentEmployee.id}`);
        if (stored) {
          const parsed = JSON.parse(stored);
          // Convert date strings back to Date objects
          Object.keys(parsed).forEach(key => {
            parsed[key] = new Date(parsed[key]);
          });
          return parsed;
        }
      } catch (error) {
        console.error('Failed to load cost centre last used:', error);
      }
      return {};
    };

    // Get all unique cost centres from projects
    const allCostCentres = new Map<string, CostCentre>();
    data.projects.forEach(project => {
      project.costCentres.forEach(cc => {
        if (!allCostCentres.has(cc.id)) {
          allCostCentres.set(cc.id, { ...cc });
        }
      });
    });

    const favouriteIds = loadCostCentreFavourites();
    const lastUsedMap = loadCostCentreLastUsed();

    const enrichedCostCentres = Array.from(allCostCentres.values()).map(cc => ({
      ...cc,
      isFavourite: favouriteIds.includes(cc.id),
      lastUsed: lastUsedMap[cc.id]
    }));

    setCostCentres(enrichedCostCentres);
  }, [currentEmployee.id, data.projects]);

  useEffect(() => {
    // Mark some projects as favourites for the current user
    const updatedProjects = data.projects.map((project, index) => ({
      ...project,
      isFavourite: index < 5,
      lastUsed: index < 10 ? new Date(Date.now() - index * 86400000) : undefined
    }));
    
    setData(prev => ({ ...prev, projects: updatedProjects }));
  }, []);

  // Generate historical timesheets with audit trails on mount
  useEffect(() => {
    const initHistoricalData = () => {
      // Check if historical data already exists
      const hasHistoricalData = localStorage.getItem('historicalDataInitialized');
      const historicalDataVersion = localStorage.getItem('historicalDataVersion');
      const CURRENT_VERSION = '2'; // Increment when schema changes (e.g., added roleId)
      
      // Regenerate if no data exists or version is outdated
      if (!hasHistoricalData || historicalDataVersion !== CURRENT_VERSION) {
        if (historicalDataVersion !== CURRENT_VERSION) {
        } else {
        }
        
        // Generate historical timesheets for the current employee
        const historicalTimesheets = generateHistoricalTimesheets(
          currentEmployee,
          data.projects
        );
        
        // Add historical timesheets to the existing timesheets
        setData(prev => ({
          ...prev,
          timesheets: [...prev.timesheets, ...historicalTimesheets]
        }));
        
        // Mark as initialized with version
        localStorage.setItem('historicalDataInitialized', 'true');
        localStorage.setItem('historicalDataVersion', CURRENT_VERSION);
        
      }
    };
    
    initHistoricalData();
  }, [currentEmployee, data.projects]);

  // Generate audit history for all submitted timesheets (for approval screen)
  useEffect(() => {
    const initAuditHistory = async () => {
      const hasAuditHistory = localStorage.getItem('auditHistoryInitialized');
      
      if (!hasAuditHistory) {
        // Dynamically import to avoid circular dependencies
        const { generateAuditHistoryForTimesheets } = await import('@/data/generateHistoricalData');
        
        // Wait a bit for timesheets to be loaded
        setTimeout(() => {
          generateAuditHistoryForTimesheets(
            data.timesheets,
            data.employees
          );
          localStorage.setItem('auditHistoryInitialized', 'true');
        }, 500);
      }
    };
    
    if (data.timesheets.length > 0 && data.employees.length > 0) {
      initAuditHistory();
    }
  }, [data.timesheets, data.employees]);

  // Generate sample rejection data on mount (for demo purposes)
  useEffect(() => {
    const initSampleRejections = async () => {
      const hasRejectionData = localStorage.getItem('sampleRejectionsInitialized');
      
      if (!hasRejectionData) {
        // Dynamically import to avoid circular dependencies
        const { generateSampleRejections } = await import('@/data/generateSampleRejections');
        
        // Wait a bit for timesheets to be loaded
        setTimeout(() => {
          generateSampleRejections(
            currentEmployee.id,
            data.timesheets,
            data.projects,
            costCentres
          );
          localStorage.setItem('sampleRejectionsInitialized', 'true');
        }, 1000);
      }
    };
    
    if (data.timesheets.length > 0 && costCentres.length > 0) {
      initSampleRejections();
    }
  }, [currentEmployee.id, data.timesheets, data.projects, costCentres]);

  const updateTimesheet = (timesheet: Timesheet) => {
    const oldTimesheet = data.timesheets.find(ts => ts.id === timesheet.id);
    const isNewTimesheet = !oldTimesheet || !timesheet.id || timesheet.id === 0;
    
    let actionType: AuditActionType = 'modified';
    let changes: AuditChange[] = [];
    
    if (oldTimesheet) {
      if (oldTimesheet.status !== timesheet.status) {
        switch (timesheet.status) {
          case 'submitted':
            actionType = 'submitted';
            break;
          case 'approved':
            actionType = 'approved';
            break;
          case 'rejected':
            actionType = 'rejected';
            break;
          case 'locked':
            actionType = 'locked';
            break;
          default:
            actionType = 'saved_draft';
        }
      } else {
        changes = auditService.detectChanges(oldTimesheet, timesheet);
        if (changes.length > 0) {
          actionType = 'modified';
        } else {
          actionType = 'saved_draft';
        }
      }
      
      const actorRole = timesheet.approverId === currentEmployee.id ? 'manager' : 'employee';
      
      auditService.logAction(
        timesheet.id,
        actionType,
        currentEmployee.id,
        currentEmployee.name,
        {
          previousStatus: oldTimesheet.status,
          newStatus: timesheet.status,
          changes: changes.length > 0 ? changes : undefined,
          comment: timesheet.rejectionReason,
          actorRole,
        }
      );
    } else {
      auditService.logAction(
        timesheet.id,
        'created',
        currentEmployee.id,
        currentEmployee.name,
        {
          newStatus: timesheet.status,
          actorRole: 'employee',
        }
      );
    }
    
    if (isNewTimesheet) {
      if (isDemo) {
        setData(prev => ({
          ...prev,
          timesheets: [...prev.timesheets, timesheet]
        }));
      }
    } else {
      setData(prev => ({
        ...prev,
        timesheets: prev.timesheets.map(ts => 
          ts.id === timesheet.id ? timesheet : ts
        )
      }));
    }
    
    if (mode === 'live') {
      if (isNewTimesheet) {
        const timesheetToCreate = { ...timesheet, id: 0, companyId: timesheet.companyId || currentCompanyId };
        timesheetApi.createTimesheet(timesheetToCreate)
          .then((created) => {
            setData(prev => ({
              ...prev,
              timesheets: [...prev.timesheets, created]
            }));
          })
          .catch((err) => {
            console.error('❌ Failed to create timesheet via API:', err);
            toast.error('Failed to save to server', {
              description: 'Could not create timesheet on server.',
            });
          });
      } else {
        timesheetApi.updateTimesheet(timesheet.id, timesheet)
          .then(() => {
          })
          .catch((err) => {
            console.error('❌ Failed to update timesheet via API:', err);
            toast.error('Failed to save to server', {
              description: 'Changes saved locally but could not sync with server.',
            });
          });
      }
    }
  };

  const deleteTimesheet = (id: number) => {
    setData(prev => ({
      ...prev,
      timesheets: prev.timesheets.filter(ts => ts.id !== id)
    }));
  };

  const toggleProjectFavourite = (projectId: string) => {
    setData(prev => ({
      ...prev,
      projects: prev.projects.map(p =>
        p.id === projectId ? { ...p, isFavourite: !p.isFavourite } : p
      )
    }));
  };

  const toggleCostCentreFavourite = (costCentreId: string) => {
    setCostCentres(prev => {
      const updated = prev.map(cc =>
        cc.id === costCentreId ? { ...cc, isFavourite: !cc.isFavourite } : cc
      );

      // Save to localStorage
      const favouriteIds = updated.filter(cc => cc.isFavourite).map(cc => cc.id);
      try {
        localStorage.setItem(
          `costCentreFavourites_${currentEmployee.id}`,
          JSON.stringify(favouriteIds)
        );
      } catch (error) {
        console.error('Failed to save cost centre favourites:', error);
      }

      return updated;
    });
  };

  const updateCostCentreLastUsed = (costCentreId: string) => {
    setCostCentres(prev => {
      const updated = prev.map(cc =>
        cc.id === costCentreId ? { ...cc, lastUsed: new Date() } : cc
      );

      // Save to localStorage
      const lastUsedMap: Record<string, string> = {};
      updated.forEach(cc => {
        if (cc.lastUsed) {
          lastUsedMap[cc.id] = cc.lastUsed.toISOString();
        }
      });
      try {
        localStorage.setItem(
          `costCentreLastUsed_${currentEmployee.id}`,
          JSON.stringify(lastUsedMap)
        );
      } catch (error) {
        console.error('Failed to save cost centre last used:', error);
      }

      return updated;
    });
  };

  const switchEmployee = (employeeId: number) => {
    const employee = data.employees.find(emp => emp.id === employeeId);
    if (employee) {
      setCurrentEmployee(employee);
      setCurrentEmployeeId(employeeId);
      localStorage.setItem('currentEmployeeId', String(employeeId));
      
      if (liveData !== null) {
        setLiveData(null);
      }
      // Reset team timesheets loaded flag so they will be re-fetched for the new employee
      setTeamTimesheetsLoaded(false);
    }
  };

  // Set current employee directly from auth - bypasses the need to look up in employee list
  // This is used by useAuthenticatedEmployee hook when liveData may not be loaded yet
  const setCurrentEmployeeFromAuth = (employee: Employee) => {
    if (!employee?.id) {
      console.warn('[setCurrentEmployeeFromAuth] Invalid employee object');
      return;
    }

    setCurrentEmployee(employee);
    setCurrentEmployeeId(employee.id);
    localStorage.setItem('currentEmployeeId', String(employee.id));

    // Force reload of live data for the new employee
    if (liveData !== null) {
      setLiveData(null);
    }

    // Trigger live data loading for the new employee
    // Pass the employee ID directly to avoid race condition with async state updates
    // This ensures the employee picker has the correct list
    ensureLiveDataLoaded(employee.id);
  };

  const updateTimesheetForMode= (mode: 'demo' | 'live', timesheet: Timesheet) => {
    const isNewTimesheet = !timesheet.id || timesheet.id === 0;
    
    if (mode === 'demo') {
      if (isNewTimesheet) {
        setDemoData(prev => ({
          ...prev,
          timesheets: [...prev.timesheets, timesheet]
        }));
        setData(prev => ({
          ...prev,
          timesheets: [...prev.timesheets, timesheet]
        }));
      } else {
        setDemoData(prev => ({
          ...prev,
          timesheets: prev.timesheets.map(ts => 
            ts.id === timesheet.id ? timesheet : ts
          )
        }));
        setData(prev => ({
          ...prev,
          timesheets: prev.timesheets.map(ts => 
            ts.id === timesheet.id ? timesheet : ts
          )
        }));
      }
    } else {
      if (isNewTimesheet) {
        const timesheetToCreate = { ...timesheet, id: 0, companyId: timesheet.companyId || currentCompanyId };
        timesheetApi.createTimesheet(timesheetToCreate)
          .then((created) => {
            if (liveData) {
              setLiveData(prev => prev ? ({
                ...prev,
                timesheets: [...prev.timesheets, created]
              }) : prev);
            }
            setData(prev => ({
              ...prev,
              timesheets: [...prev.timesheets, created]
            }));
            toast.success('Timesheet created successfully');
          })
          .catch((err) => {
            console.error('❌ Failed to create timesheet via API:', err);
            toast.error('Failed to save to server', {
              description: 'Could not create timesheet on server.',
            });
          });
      } else {
        if (liveData) {
          setLiveData(prev => {
            if (!prev) return prev;
            const exists = prev.timesheets.some(ts => ts.id === timesheet.id);
            return {
              ...prev,
              timesheets: exists
                ? prev.timesheets.map(ts => ts.id === timesheet.id ? timesheet : ts)
                : [...prev.timesheets, timesheet],
            };
          });
        }
        setData(prev => {
          const exists = prev.timesheets.some(ts => ts.id === timesheet.id);
          return {
            ...prev,
            timesheets: exists
              ? prev.timesheets.map(ts => ts.id === timesheet.id ? timesheet : ts)
              : [...prev.timesheets, timesheet],
          };
        });
        
        timesheetApi.updateTimesheet(timesheet.id, timesheet)
          .then(() => {
          })
          .catch((err) => {
            console.error('❌ Failed to update timesheet via API:', err);
            toast.error('Failed to save to server', {
              description: 'Changes saved locally but could not sync with server.',
            });
          });
      }
    }
  };

  // Auto-configure absence settings for prototype if not already set
  React.useEffect(() => {
    const existingSettings = getAbsenceSettings();
    if (!existingSettings && data.projects.length > 0) {
      const firstProject = data.projects[0];
      const firstCostCentre = firstProject.costCentres?.[0];
      
      if (firstCostCentre) {
        const defaultSettings = {
          projectId: firstProject.id,
          projectName: firstProject.name,
          costCentreId: firstCostCentre.id,
          costCentreName: firstCostCentre.name,
        };
        
        saveAbsenceSettings(defaultSettings);
      }
    }
  }, [data.projects]);

  // Inject absence entries into timesheets
  // Use liveData.timesheets when available (live mode), otherwise use data.timesheets (demo mode)
  const baseTimesheets = liveData ? liveData.timesheets : data.timesheets;
  
  const timesheetsWithAbsences = React.useMemo(() => {
    // In LIVE mode, only use data from the database - no mock absence injection
    // Mock absence entries are only for demo/prototype purposes
    if (liveData) {
      return baseTimesheets;
    }
    
    // DEMO mode: inject mock absence entries for prototype/demo purposes
    const absenceSettings = getAbsenceSettings();
    
    if (!absenceSettings) {
      return baseTimesheets;
    }

    return baseTimesheets.map(timesheet => {
      // Generate absence entries for this timesheet's week
      const absenceEntries = generateMockAbsenceEntries(
        timesheet.employeeId,
        timesheet.weekEnding,
        absenceSettings
      );
      
      // If no absence entries, return original timesheet
      if (absenceEntries.length === 0) {
        return timesheet;
      }

      // Check if absence entries already exist (to avoid duplicates)
      const existingAbsenceIds = new Set(
        timesheet.entries
          .filter(e => 'isAbsence' in e && e.isAbsence)
          .map(e => e.id)
      );

      // Filter out absence entries that already exist
      const newAbsenceEntries = absenceEntries.filter(
        ae => !existingAbsenceIds.has(ae.id)
      );

      // If no new absence entries, return original timesheet
      if (newAbsenceEntries.length === 0) {
        return timesheet;
      }

      // Return timesheet with absence entries added
      return {
        ...timesheet,
        entries: [...timesheet.entries, ...newAbsenceEntries],
      };
    });
  }, [baseTimesheets, liveData]);

  const pendingApprovals = timesheetsWithAbsences.filter(ts => {
    if (ts.status !== 'submitted') return false;

    // Workflow / explicit approver: show when current user is the assigned approver
    if (ts.approverId) {
      return ts.approverId === currentEmployee.id;
    }

    // Legacy fallback: no explicit approver, use manager relationship
    const employee = data.employees.find(e => e.id === ts.employeeId);
    return employee?.managerId === currentEmployee.id;
  });

  // Memoize the context value to prevent unnecessary re-renders of consumers.
  // Without this, the value object is a new reference on every provider re-render,
  // which triggers all useContext consumers (and their useEffects) to re-run.
  const contextValue = useMemo(() => ({
    ...data,
    timesheets: timesheetsWithAbsences, // Use timesheets with absence entries injected
    costCentres,
    currentEmployee,
    currentUser: currentEmployee,
    pendingApprovals,
    updateTimesheet,
    deleteTimesheet,
    toggleProjectFavourite,
    toggleCostCentreFavourite,
    updateCostCentreLastUsed,
    switchEmployee,
    setCurrentEmployeeFromAuth,
    getDemoData: () => demoData,
    getLiveData: () => liveData,
    getLiveLoading: () => liveLoading,
    getLiveError: () => liveError,
    ensureLiveDataLoaded,
    refreshEmployeeTimesheets,
    updateTimesheetForMode,
    loadDirectReportTimesheets,
    teamTimesheetsLoading
  }), [
    data,
    timesheetsWithAbsences,
    costCentres,
    currentEmployee,
    pendingApprovals,
    updateTimesheet,
    deleteTimesheet,
    toggleProjectFavourite,
    toggleCostCentreFavourite,
    updateCostCentreLastUsed,
    switchEmployee,
    setCurrentEmployeeFromAuth,
    demoData,
    liveData,
    liveLoading,
    liveError,
    ensureLiveDataLoaded,
    refreshEmployeeTimesheets,
    updateTimesheetForMode,
    loadDirectReportTimesheets,
    teamTimesheetsLoading
  ]);

  return (
    <TimesheetContext.Provider value={contextValue}>
      {children}
    </TimesheetContext.Provider>
  );
};

export const useTimesheet = () => {
  const context = useContext(TimesheetContext);
  if (!context) {
    throw new Error('useTimesheet must be used within TimesheetProvider');
  }
  return context;
};

export const useTimesheetForScreen = (screenId: string, isActive = true) => {
  const context = useContext(TimesheetContext);
  const { getScreenMode } = useDataSource();
  
  if (!context) {
    throw new Error('useTimesheetForScreen must be used within TimesheetProvider');
  }
  
  const mode = getScreenMode(screenId);
  const demoData = context.getDemoData();
  const liveData = context.getLiveData();
  const liveLoading = context.getLiveLoading();
  const liveError = context.getLiveError();
  
  // Destructure ensureLiveDataLoaded for a stable dependency reference.
  // Do NOT include `context` or `liveData` in the dependency array — they change
  // on every re-render / data load, which would restart the cycle.
  const { ensureLiveDataLoaded } = context;
  
  useEffect(() => {
    if (mode === 'live' && isActive) {
      ensureLiveDataLoaded();
    }
  }, [mode, isActive, ensureLiveDataLoaded]);
  
  const selectedData = mode === 'live' ? liveData : demoData;
  const currentEmployee = selectedData?.employees.find(emp => emp.id === context.currentEmployee.id) || context.currentEmployee;
  
  const updateTimesheet = (timesheet: Timesheet) => {
    context.updateTimesheetForMode(mode, timesheet);
  };
  
  return {
    clients: selectedData?.clients || [],
    projects: selectedData?.projects || [],
    employees: selectedData?.employees || [],
    timesheets: context.timesheets || [],
    costCentres: context.costCentres,
    currentEmployee,
    currentUser: currentEmployee,
    pendingApprovals: selectedData
      ? context.timesheets.filter(ts => {
          if (ts.status !== 'submitted') return false;

          // Workflow / explicit approver: show when current user is the assigned approver
          if (ts.approverId) {
            return ts.approverId === currentEmployee.id;
          }

          // Legacy fallback: no explicit approver, use manager relationship
          const employee = selectedData.employees.find(e => e.id === ts.employeeId);
          return employee?.managerId === currentEmployee.id;
        })
      : [],
    updateTimesheet,
    deleteTimesheet: context.deleteTimesheet,
    toggleProjectFavourite: context.toggleProjectFavourite,
    toggleCostCentreFavourite: context.toggleCostCentreFavourite,
    updateCostCentreLastUsed: context.updateCostCentreLastUsed,
    switchEmployee: context.switchEmployee,
    refreshEmployeeTimesheets: context.refreshEmployeeTimesheets,
    loading: mode === 'live' ? liveLoading : false,
    error: mode === 'live' ? liveError : null
  };
};

// Alias for compatibility
export const useTimesheetContext = useTimesheet;
